import BASE_URL from "../Config";
import { store } from "../store";
import { updateAccessToken, updateRefreshToken, logout } from "../store/authSlice";
import {
  getRefreshToken,
  setRefreshToken,
  setCustomerAccessToken,
  getEmployeeRefreshToken,
  setEmployeeAccessToken,
  setEmployeeRefreshToken,
  getBusinessCardRefreshToken,
  getAdminRefreshToken,
  setAdminAccessToken,
  setAdminRefreshToken,
  removeAdminAccessToken,
  removeAdminRefreshToken,
  removeEmployeeAccessToken,
  removeEmployeeRefreshToken,
  getFreelanceRefreshToken,
  setFreelanceAccessToken,
  setFreelanceRefreshToken,
  removeFreelanceAccessToken,
  removeFreelanceRefreshToken,
  setBusinessCardAccessToken,
  setBusinessCardRefreshToken,
  removeBusinessCardAccessToken,
  removeBusinessCardRefreshToken,
} from "./cookieUtils";

let refreshTokenInterval: NodeJS.Timeout | null = null;
let pendingRefresh: Promise<boolean> | null = null;
let pendingEmployeeRefresh: Promise<boolean> | null = null;
let pendingFreelanceRefresh: Promise<boolean> | null = null;
let pendingBusinessCardRefresh: Promise<boolean> | null = null;
let pendingAdminRefresh: Promise<boolean> | null = null;
export const refreshAccessToken = async (): Promise<boolean> => {
  // Return the in-flight promise to prevent concurrent refresh calls
  if (pendingRefresh) return pendingRefresh;

  const refreshToken = getRefreshToken();
  console.log("Initiating token refresh...", { refreshToken: !!refreshToken });
  if (!refreshToken) return false;

  pendingRefresh = (async () => {
    try {
      console.log("Attempting token refresh...");
      const response = await fetch(`${BASE_URL}/user-service/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) throw new Error(`Refresh failed: ${response.status}`);

      const data = await response.json();

      const newAccessToken = data.accessToken || data.mobileNumber;
      const newRefreshToken = data.refreshToken || data.mobileOtpSession;

      if (!newAccessToken || typeof newAccessToken !== "string") {
        throw new Error("Refresh token returned no access token");
      }

      setCustomerAccessToken(newAccessToken);
      store.dispatch(updateAccessToken(newAccessToken));

      if (newRefreshToken && typeof newRefreshToken === "string") {
        setRefreshToken(newRefreshToken);
        store.dispatch(updateRefreshToken(newRefreshToken));
      }

      return true;
    } catch {
      store.dispatch(logout());
      return false;
    } finally {
      pendingRefresh = null;
    }
  })();

  return pendingRefresh;
};
export const refreshEmployeeAccessToken = async (): Promise<boolean> => {
  if (pendingEmployeeRefresh) return pendingEmployeeRefresh;

  const refreshToken = getEmployeeRefreshToken();

  if (!refreshToken) return false;

  pendingEmployeeRefresh = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/user-service/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok)
        throw new Error(`Employee refresh failed: ${response.status}`);

      const data = await response.json();

      if (data.mobileNumber) {
        setEmployeeAccessToken(data.mobileNumber);
      }

      if (data.mobileOtpSession) {
        setEmployeeRefreshToken(data.mobileOtpSession);
      }

      return true;
    } catch (error) {
      removeEmployeeAccessToken();
      removeEmployeeRefreshToken();

      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("redirectPath", currentPath);
      window.location.href = "/userlogin";

      return false;
    } finally {
      pendingEmployeeRefresh = null;
    }
  })();

  return pendingEmployeeRefresh;
};
export const refreshBusinessCardAccessToken = async (): Promise<boolean> => {
  if (pendingBusinessCardRefresh) return pendingBusinessCardRefresh;

  const refreshToken = getBusinessCardRefreshToken();
  if (!refreshToken) return false;

  pendingBusinessCardRefresh = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/user-service/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error(`Business Card refresh failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.mobileNumber) {
        setBusinessCardAccessToken(data.mobileNumber);
      }

      if (data.mobileOtpSession) {
        setBusinessCardRefreshToken(data.mobileOtpSession);
      }

      return true;
    } catch {
      removeBusinessCardAccessToken();
      removeBusinessCardRefreshToken();

      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("redirectPath", currentPath);
      window.location.href = "/business-card/login";

      return false;
    } finally {
      pendingBusinessCardRefresh = null;
    }
  })();

  return pendingBusinessCardRefresh;
};

export const refreshAdminAccessToken = async (): Promise<boolean> => {
  if (pendingAdminRefresh) return pendingAdminRefresh;

  const refreshToken = getAdminRefreshToken();
  if (!refreshToken) return false;

  pendingAdminRefresh = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/user-service/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error(`AskOxy Admin refresh failed: ${response.status}`);
      }

      const data = await response.json();

      // This endpoint currently returns the new access/refresh JWTs under
      // mobileNumber/mobileOtpSession. Keep the conventional field names as
      // fallbacks so the admin flow also survives a backend response migration.
      const newAccessToken = data.accessToken || data.mobileNumber;
      const newRefreshToken = data.refreshToken || data.mobileOtpSession;

      // A 200 response without a replacement access token is not a successful
      // refresh: retrying with the expired token would only produce another 401.
      if (!newAccessToken || typeof newAccessToken !== "string") {
        throw new Error("AskOxy Admin refresh returned no access token");
      }

      setAdminAccessToken(newAccessToken);
      if (newRefreshToken && typeof newRefreshToken === "string") {
        setAdminRefreshToken(newRefreshToken);
      }

      return true;
    } catch {
      removeAdminAccessToken();
      removeAdminRefreshToken();
      return false;
    } finally {
      pendingAdminRefresh = null;
    }
  })();

  return pendingAdminRefresh;
};
export const refreshFreelanceAccessToken = async (): Promise<boolean> => {
  if (pendingFreelanceRefresh) return pendingFreelanceRefresh;

  const refreshToken = getFreelanceRefreshToken();

  if (!refreshToken) return false;

  pendingFreelanceRefresh = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/user-service/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok)
        throw new Error(`Freelance refresh failed: ${response.status}`);

      const data = await response.json();

      if (data.mobileNumber) {
        setFreelanceAccessToken(data.mobileNumber);
      }

      if (data.mobileOtpSession) {
        setFreelanceRefreshToken(data.mobileOtpSession);
      }

      return true;
    } catch (error) {
      removeFreelanceAccessToken();
      removeFreelanceRefreshToken();
      sessionStorage.removeItem("userId");
      sessionStorage.removeItem("Name");
      sessionStorage.removeItem("primaryType");

      const currentPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("redirectPath", currentPath);
      window.location.href = "/employee-login";

      return false;
    } finally {
      pendingFreelanceRefresh = null;
    }
  })();

  return pendingFreelanceRefresh;
};

export const startTokenRefresh = (): void => {
  stopTokenRefresh();
  if (!getRefreshToken()) return;

  refreshAccessToken(); // immediate call on start

  refreshTokenInterval = setInterval(() => {
    refreshAccessToken();
  }, 5 * 60 * 1000); // every 5 minutes
};

export const stopTokenRefresh = (): void => {
  if (refreshTokenInterval) {
    clearInterval(refreshTokenInterval);
    refreshTokenInterval = null;
  }
};

export const isAuthenticated = (): boolean =>
  !!(store.getState().auth.accessToken && getRefreshToken());
